import { useCallback, useMemo, useRef, useState } from "react";
import { getCategoriesByID, getSubFilterBykeyFilter } from "../../auth/apiCategory";

// Filtres dynamiques d'une catégorie Elakay (getCategoriesByID -> data.filter,
// sous-filtres via getSubFilterBykeyFilter) -- même contrat et mêmes règles que
// l'étape "Filters" de Sell Your Product (pages/addProduct.jsx : createdFilter /
// addBatchAfterName / removeBatchFromFilters / handleFilter), mais en état React
// au lieu d'objets module-level, pour pouvoir servir plusieurs flux (Provider
// maintenant ; Seller pourra migrer dessus sans changer de comportement).

// Catégorie racine "Elakay Categories" -- même valeur que addProduct.jsx.
export const ELAKAY_ROOT_CATEGORY_ID = "5ed4946fd2ba8812c20261a8";

export const keySpaceClean = (key) =>
  String(key || "").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9_]/g, "");

const filterKey = (item) => keySpaceClean(item?._id?.name || item?._id?.lang?.en?.name);
const idOf = (it) => String(it?._id?.["_id"] ?? it?._id);
// ancien format { _id:{...}, Filters:[] } ; les sous-filtres arrivent à plat.
const normalize = (it) =>
  it?._id && typeof it._id === "object" && "_id" in it._id
    ? { ...it, Filters: Array.isArray(it.Filters) ? it.Filters : [] }
    : { _id: { ...it }, Filters: [] };

export const isSubFilterTrigger = (subFilter, value) =>
  Array.isArray(subFilter) &&
  typeof value === "string" &&
  subFilter.some((item) => String(item.keyFilter) === keySpaceClean(value));

const useCategoryFilters = () => {
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const subTriggers = useRef({}); // name -> valeur qui a déclenché le sous-filtre
  const requestSeq = useRef(0);

  const loadCategory = useCallback(async (categoryId = ELAKAY_ROOT_CATEGORY_ID) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    subTriggers.current = {};
    try {
      const data = await getCategoriesByID(categoryId);
      if (seq !== requestSeq.current) return null; // réponse périmée
      if (!data || data.error) throw new Error("data Not Found");
      setCategory(data.cartegory);
      setItems(data.items || []);
      setFilters(data.filter || []);
      setSelected({});
      return data;
    } catch (err) {
      console.error("Category load failed:", err);
      return null;
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  const insertAfter = (batch, afterName) =>
    setFilters((prev) => {
      const cur = (prev || []).map(normalize);
      const known = new Set(cur.map(idOf));
      const toInsert = (batch || []).map(normalize).filter((b) => !known.has(idOf(b)));
      if (!toInsert.length) return cur;
      const anchor = cur.findIndex((it) => filterKey(it) === afterName);
      return anchor === -1
        ? [...cur, ...toInsert]
        : [...cur.slice(0, anchor + 1), ...toInsert, ...cur.slice(anchor + 1)];
    });

  const removeBatch = (batch) => {
    const ids = new Set((batch || []).map(idOf));
    setFilters((prev) => (prev || []).filter((it) => !ids.has(idOf(it))));
  };

  const select = useCallback(async (name, filterDef, rawValue) => {
    const value = String(rawValue || "").trim();
    setSelected((prev) => ({ ...prev, [name]: value }));

    const previousTrigger = subTriggers.current[name];
    if (previousTrigger && previousTrigger !== value) {
      delete subTriggers.current[name];
      const old = await getSubFilterBykeyFilter(filterDef._id, previousTrigger);
      if (old && !old.error) removeBatch(old);
    }
    if (isSubFilterTrigger(filterDef.subFilter, value)) {
      subTriggers.current[name] = value;
      const sub = await getSubFilterBykeyFilter(filterDef._id, value);
      if (sub && !sub.error) insertAfter(sub, name);
    }
  }, []);

  const validKeys = useMemo(() => filters.map(filterKey).filter(Boolean), [filters]);
  // Sélection restreinte aux filtres actuellement affichés (un sous-filtre retiré
  // ne doit jamais laisser sa valeur dans le payload) -- équivalent de createdFilter().
  const activeSelection = useMemo(() => {
    const out = {};
    validKeys.forEach((key) => {
      out[key] = selected[key] || "";
    });
    return out;
  }, [validKeys, selected]);

  return { category, items, filters, selected, activeSelection, loading, loadCategory, select, setSelected };
};

export default useCategoryFilters;
